import type { Auth } from 'firebase/auth';
import type { Firestore } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { runFirestoreRulesDiagnostics } from './firestoreRulesDiagnosticsService';

const importMetaEnv = (import.meta as any).env || {};

const BACKEND_BASE_URL =
  importMetaEnv.VITE_BACKEND_BASE_URL ||
  importMetaEnv.VITE_FUNCTIONS_BASE_URL ||
  'https://mintleaf-foglalasok-4-ai-copy.onrender.com';

export type PermissionDoctorOptions = {
  unitId?: string;
  divisionId?: string;
  appId?: 'requests' | 'schedule' | 'bookings' | 'timeEntries' | 'users';
  operation?: string;
  targetUserId?: string;
  payload?: unknown;
};

export type PermissionFailureContext = {
  appId: string;
  operation: string;
  path?: string;
  method?: string;
  payload?: unknown;
  errorCode?: string;
  errorMessage?: string;
  unitId?: string;
  targetUserId?: string;
  permissionScope?: unknown;
};

type AccessProfileReport = {
  exists: boolean;
  readStatus: 'success' | 'not-found' | 'permission-denied' | 'error';
  appUserId: string | null;
  canonicalUserId: string | null;
  role: string | null;
  unitIds: string[];
  disabled: boolean | null;
  suspended: boolean | null;
};

type PermissionDoctorReport = {
  timestamp: string;
  environment: Record<string, unknown>;
  firebaseAuth: { uid: string | null; email: string | null };
  currentAppUserId: string | null;
  accessProfile: AccessProfileReport;
  selectedUnitId: string | null;
  selectedDivisionId: string | null;
  activeUnitIds: string[];
  backendEffectivePermissions: Record<string, unknown>;
  firestoreRulesDiagnostics: Record<string, unknown>;
  operationDiagnostics: Record<string, unknown>;
  lastFailedPermissionOperation: PermissionFailureContext | null;
  likelyRootCause: string;
  recommendedFix: string;
  severity: 'info' | 'warning' | 'error';
};

declare global {
  interface Window {
    __mintleafPermissionDoctor?: (options?: PermissionDoctorOptions) => Promise<PermissionDoctorReport>;
    __mintleafPermissionDoctorLast?: () => PermissionDoctorReport | null;
    __mintleafPermissionDoctorCopyLast?: () => Promise<boolean>;
  }
}

let installedAuth: Auth | null = null;
let installedDb: Firestore | null = null;
let lastFailure: PermissionFailureContext | null = null;
let lastReport: PermissionDoctorReport | null = null;

const normalizeUnitIds = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((unitId): unitId is string => typeof unitId === 'string' && unitId.length > 0);
  return typeof value === 'string' && value.length > 0 ? [value] : [];
};

const errorInfo = (error: any) => ({
  code: typeof error?.code === 'string' ? error.code.replace(/^firestore\//, '') : 'unknown',
  message: typeof error?.message === 'string' ? error.message : String(error),
});

const safeGetDoc = async (db: Firestore, path: string) => {
  try {
    const snap = await getDoc(doc(db, path));
    return { path, status: snap.exists() ? 'success' : 'not-found', data: snap.exists() ? snap.data() : null };
  } catch (error: any) {
    const info = errorInfo(error);
    return { path, status: info.code === 'permission-denied' ? 'permission-denied' : 'error', ...info, data: null };
  }
};

const readAccessProfile = async (db: Firestore, authUid: string | null): Promise<AccessProfileReport> => {
  if (!authUid) return { exists: false, readStatus: 'permission-denied', appUserId: null, canonicalUserId: null, role: null, unitIds: [], disabled: null, suspended: null };
  const result = await safeGetDoc(db, `access_profiles/${authUid}`);
  const data = result.data as Record<string, any> | null;
  return {
    exists: result.status === 'success',
    readStatus: result.status as AccessProfileReport['readStatus'],
    appUserId: typeof data?.appUserId === 'string' ? data.appUserId : null,
    canonicalUserId: typeof data?.canonicalUserId === 'string' ? data.canonicalUserId : null,
    role: typeof data?.role === 'string' ? data.role : null,
    unitIds: [...normalizeUnitIds(data?.unitIds), ...normalizeUnitIds(data?.unitIDs), ...normalizeUnitIds(data?.unitId)],
    disabled: typeof data?.disabled === 'boolean' ? data.disabled : null,
    suspended: typeof data?.suspended === 'boolean' ? data.suspended : null,
  };
};

const operationCatalog = {
  requests: ['requests.create', 'requests.update', 'requests.delete', 'requests.review'],
  schedule: ['schedule.read', 'schedule.write', 'schedule.settings.read', 'schedule.settings.write', 'schedule.displaySettings.write', 'schedule.exportSettings.read', 'schedule.exportSettings.write'],
  bookings: ['bookings.read', 'bookings.create', 'bookings.update', 'bookings.cancel', 'reservationSettings.read', 'reservationSettings.write'],
  timeEntries: ['timeEntries.read', 'timeEntries.create', 'timeEntries.update', 'timeEntries.delete'],
  users: ['users.read'],
};

const buildOperationDiagnostics = async (db: Firestore, authUid: string | null, options: PermissionDoctorOptions, profile: AccessProfileReport) => {
  const unitId = options.unitId || null;
  const targetUserId = options.targetUserId || null;
  const userHasUnit = !!unitId && profile.unitIds.includes(unitId);
  // Firestore Rules diagnostic probe documents were removed to reduce rules complexity.
  const probes: Awaited<ReturnType<typeof safeGetDoc>>[] = [];
  const checks: string[] = [];
  if (!authUid) checks.push('is signed in: false');
  if (unitId) checks.push(`access profile has unit: ${userHasUnit}`);
  if (targetUserId) checks.push('target user belongs to unit: read-only verification requires backend/user profile data');
  if (options.operation?.startsWith('schedule.')) checks.push('schedule writes should use backend API; settings doc ids must use exact unitId and `${unitId}_${weekStartDate}`');
  if (options.operation?.startsWith('bookings.') || options.operation?.startsWith('reservationSettings.')) checks.push('protected booking/reservation writes should not be direct client writes unless rules explicitly allow the unit/role path');
  if (options.operation?.startsWith('requests.')) checks.push('request create must match own/admin/reviewer flow; createdBy/userId must align with identity and target unit');
  if (options.operation?.startsWith('timeEntries.')) checks.push('time entry payload userId/unitId must match self or admin/leader unit overlap');
  return { supportedApps: operationCatalog, requestedOperationSupported: !options.appId || operationCatalog[options.appId]?.includes(options.operation || ''), probes, checks, payloadEcho: options.payload ?? null };
};


const fetchBackendEffectivePermissions = async (auth: Auth, options: PermissionDoctorOptions, profile: AccessProfileReport) => {
  const appUserId = profile.appUserId || profile.canonicalUserId;
  if (!auth.currentUser) return { status: 'skipped', reason: 'AUTH_MISSING' };
  if (!appUserId) return { status: 'skipped', reason: 'ACCESS_PROFILE_MISSING' };
  const token = await auth.currentUser.getIdToken();
  const search = new URLSearchParams();
  if (options.unitId) search.set('unitId', options.unitId);
  if (options.divisionId) search.set('divisionId', options.divisionId);
  if (options.appId) search.set('appId', options.appId);
  const url = `${BACKEND_BASE_URL}/api/users/${encodeURIComponent(appUserId)}/effective-permissions${search.toString() ? `?${search.toString()}` : ''}`;
  try {
    const response = await fetch(url, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      return { status: response.status === 403 ? 'permission-denied' : 'error', httpStatus: response.status, source: 'api/users/:id/effective-permissions', error: payload?.message || 'BACKEND_PERMISSION_PROBE_FAILED', payload };
    }
    return {
      status: 'success',
      source: 'api/users/:id/effective-permissions',
      authUid: auth.currentUser.uid,
      appUserId: profile.appUserId,
      canonicalUserId: profile.canonicalUserId,
      role: profile.role,
      unitIds: profile.unitIds,
      appId: options.appId || null,
      unitId: options.unitId || null,
      divisionId: options.divisionId || null,
      effectivePermissions: payload?.effectivePermissions || {},
      sources: payload?.sources || {},
      raw: payload,
    };
  } catch (error: any) {
    return { status: 'error', source: 'api/users/:id/effective-permissions', error: errorInfo(error) };
  }
};

const classify = (authUid: string | null, options: PermissionDoctorOptions, profile: AccessProfileReport, rulesRootCause?: unknown, backendProbe?: any) => {
  if (!authUid) return ['AUTH_MISSING', 'Sign in before retrying the operation.', 'error'] as const;
  if (!profile.exists) return ['ACCESS_PROFILE_MISSING', `Create/read access_profiles/${authUid} for this Firebase auth user.`, 'error'] as const;
  if (!Array.isArray(profile.unitIds)) return ['ACCESS_PROFILE_UNITIDS_NOT_LIST', 'access_profiles/{authUid}.unitIds must be a list.', 'error'] as const;
  if (options.unitId && !profile.unitIds.includes(options.unitId)) return ['USER_NOT_IN_UNIT', 'access_profiles/{authUid}.unitIds must contain selected unitId.', 'error'] as const;
  const payload = options.payload as any;
  if (payload && options.unitId && typeof payload.unitId === 'string' && payload.unitId !== options.unitId) return ['PAYLOAD_UNIT_MISMATCH', 'payload.unitId must equal the selected Permission Doctor unitId.', 'error'] as const;
  if (payload && options.targetUserId && typeof payload.userId === 'string' && payload.userId !== options.targetUserId) return ['PAYLOAD_USER_MISMATCH', 'payload.userId must match targetUserId for this diagnostic run.', 'error'] as const;
  if (payload && typeof payload.createdBy === 'string' && profile.appUserId && payload.createdBy !== profile.appUserId && payload.createdBy !== profile.canonicalUserId) return ['CREATED_BY_MISMATCH', 'payload.createdBy must resolve to the current app/canonical user id for own writes.', 'error'] as const;
  if (backendProbe?.status === 'permission-denied') return ['BACKEND_PERMISSION_DENIED', 'Backend effective-permissions endpoint returned 403; inspect backend source decisions for this app/unit.', 'error'] as const;
  if (lastFailure?.errorCode?.includes('permission-denied') || lastFailure?.errorCode === '403') {
    const directWrite = /add|set|update|delete|batch|direct/i.test(lastFailure.method || '');
    return directWrite ? ['DIRECT_CLIENT_WRITE_BLOCKED', 'Move this write through the backend API; Firestore Rules deny direct client writes.', 'error'] as const : ['FIRESTORE_RULE_DENIED', 'Check the denied path against Firestore Rules or move the write through the backend API.', 'error'] as const;
  }
  if (typeof rulesRootCause === 'string' && rulesRootCause !== 'other') return ['FIRESTORE_RULE_DENIED', String(rulesRootCause), 'error'] as const;
  if (!options.payload) return ['UNKNOWN_NEEDS_PAYLOAD', 'Pass operation payload to diagnose createdBy/userId/unitId mismatches.', 'warning'] as const;
  return ['UNKNOWN_NEEDS_PAYLOAD', 'No obvious auth/unit failure detected; compare operation payload with the relevant app permission scope.', 'warning'] as const;
};

export const rememberPermissionFailure = (context: PermissionFailureContext): void => {
  lastFailure = { ...context };
};

const isPermissionDeniedError = (error: any): boolean => {
  const code = typeof error?.code === 'string' ? error.code : '';
  const status = error?.status || error?.statusCode;
  const message = typeof error?.message === 'string' ? error.message.toLowerCase() : '';
  return code === 'permission-denied' || code === 'firestore/permission-denied' || status === 403 || message.includes('permission-denied') || message.includes('permission denied') || message.includes('missing or insufficient permissions');
};

export const rememberPermissionDenied = (error: unknown, context: Omit<PermissionFailureContext, 'errorCode' | 'errorMessage'>): boolean => {
  if (!isPermissionDeniedError(error)) return false;
  const anyError = error as any;
  rememberPermissionFailure({
    ...context,
    errorCode: typeof anyError?.code === 'string' ? anyError.code : typeof anyError?.statusCode === 'number' ? String(anyError.statusCode) : typeof anyError?.status === 'number' ? String(anyError.status) : undefined,
    errorMessage: typeof anyError?.message === 'string' ? anyError.message : String(error),
  });
  return true;
};

export const getLastPermissionFailure = (): PermissionFailureContext | null => lastFailure;

export const copyPermissionDoctorReport = async (report: PermissionDoctorReport): Promise<boolean> => {
  const text = JSON.stringify(report, null, 2);
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  console.info('[permission-doctor] copy fallback', text);
  return false;
};

export const runPermissionDoctor = async (options: PermissionDoctorOptions = {}): Promise<PermissionDoctorReport> => {
  if (!installedAuth || !installedDb) throw new Error('Permission Doctor is not installed. Call installPermissionDoctor(auth, db) first.');
  const authUid = installedAuth.currentUser?.uid || null;
  const profile = await readAccessProfile(installedDb, authUid);
  const selectedUnitId = options.unitId || profile.unitIds[0] || null;
  const resolvedOptions = { ...options, unitId: selectedUnitId || options.unitId };
  const [rulesDiagnostics, operationDiagnostics, backendEffectivePermissions] = await Promise.all([
    selectedUnitId ? runFirestoreRulesDiagnostics(installedAuth, installedDb, selectedUnitId).catch(error => ({ error: errorInfo(error) })) : Promise.resolve({ skipped: 'unitId missing' }),
    buildOperationDiagnostics(installedDb, authUid, resolvedOptions, profile),
    fetchBackendEffectivePermissions(installedAuth, resolvedOptions, profile),
  ]);
  const [likelyRootCause, recommendedFix, severity] = classify(authUid, resolvedOptions, profile, (rulesDiagnostics as any).rootCause, backendEffectivePermissions);
  const report: PermissionDoctorReport = {
    timestamp: new Date().toISOString(),
    environment: { mode: import.meta.env.MODE, dev: import.meta.env.DEV, prod: import.meta.env.PROD, appVersion: import.meta.env.VITE_APP_VERSION || null },
    firebaseAuth: { uid: authUid, email: installedAuth.currentUser?.email || null },
    currentAppUserId: profile.appUserId || profile.canonicalUserId,
    accessProfile: profile,
    selectedUnitId,
    selectedDivisionId: options.divisionId || null,
    activeUnitIds: profile.unitIds,
    backendEffectivePermissions: backendEffectivePermissions as Record<string, unknown>,
    firestoreRulesDiagnostics: rulesDiagnostics as Record<string, unknown>,
    operationDiagnostics,
    lastFailedPermissionOperation: lastFailure,
    likelyRootCause,
    recommendedFix,
    severity,
  };
  lastReport = report;
  console.group('[permission-doctor] MintLeaf Permission Doctor v1');
  console.info('[permission-doctor] report', report);
  console.info('[permission-doctor] likelyRootCause', likelyRootCause, 'recommendedFix', recommendedFix);
  console.groupEnd();
  return report;
};

export const installPermissionDoctor = (auth: Auth, db: Firestore): void => {
  installedAuth = auth;
  installedDb = db;
  if (typeof window === 'undefined') return;
  window.__mintleafPermissionDoctor = runPermissionDoctor;
  window.__mintleafPermissionDoctorLast = () => lastReport;
  window.__mintleafPermissionDoctorCopyLast = () => lastReport ? copyPermissionDoctorReport(lastReport) : Promise.resolve(false);
};
